// Mini renderer Markdown → éléments React (pas de dangerouslySetInnerHTML)
// Gère : titres (## / ###), listes, tableaux pipe, gras/italique/code inline, séparateurs.
// Le rendu en JSX garantit l'échappement automatique de tout contenu utilisateur,
// supprimant la surface d'attaque XSS.

// Tokenise les marqueurs inline **bold**, *italic*, _italic_ et `code` en nœuds React.
function parseInline(text, keyPrefix) {
  if (!text) return [];
  const tokens = [];
  // Une regex unique qui capture tous les marqueurs en alternance
  const re = /(`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*|_[^_]+_)/g;
  let lastIndex = 0;
  let match;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      tokens.push(text.slice(lastIndex, match.index));
    }
    const t = match[0];
    const key = `${keyPrefix}-i${i++}`;
    if (t.startsWith("**")) {
      tokens.push(
        <strong key={key} className="font-bold text-[#15294F]">
          {t.slice(2, -2)}
        </strong>
      );
    } else if (t.startsWith("`")) {
      tokens.push(
        <code
          key={key}
          className="px-1 py-0.5 bg-slate-100 text-[#2D6BE0] rounded font-mono text-[0.85em] font-bold"
        >
          {t.slice(1, -1)}
        </code>
      );
    } else if (t.startsWith("*") || t.startsWith("_")) {
      tokens.push(<em key={key}>{t.slice(1, -1)}</em>);
    }
    lastIndex = match.index + t.length;
  }
  if (lastIndex < text.length) tokens.push(text.slice(lastIndex));
  return tokens;
}

export default function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let listBuf = null; // { items: [<li>], key }
  let tableBuf = null; // { headers, rows, key }

  const flushList = () => {
    if (listBuf) {
      out.push(
        <ul key={listBuf.key} className="list-disc pl-5 my-1 space-y-0.5">
          {listBuf.items}
        </ul>
      );
      listBuf = null;
    }
  };

  const flushTable = () => {
    if (tableBuf) {
      out.push(
        <div
          key={tableBuf.key}
          className="overflow-x-auto my-2 rounded-md border border-slate-200"
        >
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50">
                {tableBuf.headers.map((h, hi) => (
                  <th
                    key={hi}
                    className="px-2 py-1.5 text-left font-bold text-[#15294F]"
                  >
                    {parseInline(h, `${tableBuf.key}-h${hi}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{tableBuf.rows}</tbody>
          </table>
        </div>
      );
      tableBuf = null;
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    const k = `l${i}`;

    // Détection début de tableau : | en-têtes | suivi d'une ligne séparatrice
    if (
      /^\s*\|.+\|\s*$/.test(ln) &&
      i + 1 < lines.length &&
      /^\s*\|[\s\-:|]+\|\s*$/.test(lines[i + 1])
    ) {
      flushList();
      const headers = ln.split("|").slice(1, -1).map((c) => c.trim());
      tableBuf = { headers, rows: [], key: k };
      i++; // saute la ligne séparatrice
      continue;
    }

    // Ligne de tableau en cours
    if (tableBuf) {
      if (/^\s*\|.+\|\s*$/.test(ln)) {
        const cells = ln.split("|").slice(1, -1).map((c) => c.trim());
        const isBold = cells.some((c) => c.startsWith("**") && c.endsWith("**"));
        tableBuf.rows.push(
          <tr
            key={k}
            className={`border-t border-slate-100 ${
              isBold ?"bg-slate-50 font-semibold" :""
            }`}
          >
            {cells.map((c, ci) => (
              <td key={ci} className="px-2 py-1.5 text-slate-700">
                {parseInline(c, `${k}-c${ci}`)}
              </td>
            ))}
          </tr>
        );
        continue;
      }
      flushTable();
    }

    // Titres
    if (/^###?\s/.test(ln)) {
      flushList();
      const lvl = ln.startsWith("###") ? 3 : 2;
      const txt = ln.replace(/^#+\s/,"");
      const cls = `font-heading font-bold text-[#15294F] mt-3 mb-1 ${
        lvl === 2 ?"text-base" :"text-sm"
      }`;
      if (lvl === 2) {
        out.push(<h2 key={k} className={cls}>{parseInline(txt, k)}</h2>);
      } else {
        out.push(<h3 key={k} className={cls}>{parseInline(txt, k)}</h3>);
      }
      continue;
    }

    // Listes à puces
    if (/^\s*[-·*]\s+/.test(ln)) {
      if (!listBuf) listBuf = { items: [], key: k };
      const itemTxt = ln.replace(/^\s*[-·*]\s+/,"");
      listBuf.items.push(
        <li key={k} className="text-slate-700">
          {parseInline(itemTxt, k)}
        </li>
      );
      continue;
    }
    flushList();

    // Séparateur
    if (/^\s*---+\s*$/.test(ln)) {
      out.push(<hr key={k} className="my-3 border-slate-200" />);
      continue;
    }

    // Ligne vide → petit espace
    if (ln.trim() ==="") {
      out.push(<div key={k} className="h-1.5" />);
      continue;
    }

    // Paragraphe
    out.push(
      <p key={k} className="text-sm text-slate-700 leading-relaxed">
        {parseInline(ln, k)}
      </p>
    );
  }

  flushList();
  flushTable();
  return out;
}
